import React, { useContext, useState, useEffect, } from 'react';
import { Descriptions, Form, Row, Space, Button, Select, Typography, Divider, Alert, Input, } from 'antd';
import { SaveOutlined, WarningFilled, } from '@ant-design/icons';
import { Card } from '../../../UI/Card/Card.component';
import { StationContext } from '../../../../context/station-context';
import { NocItemContext } from '../../../../context/noc-item-context';
import { saveProfileSettings } from '../../../../services/StationService';
import { Modal } from '../../../UI/Modal/Modal.component';
import { useNotificationApi } from '../../../UI/NotificationProvider';

const { Paragraph, } = Typography;
const { Item } = Descriptions;

const labelStyle = {
    fontWeight: 800,
};

export const StationProfile = ({displayEditProfileForm, displayFields, setDisplayEditProfileForm, dataSource, ...props}) => {

    let defaultProfileSettingsObj = {
        Customer: null,
        Brand: null,
        POS: null,
        Dispenser: null,
        Payment: null,
    }

    const [form] = Form.useForm();
    const [clearProfilesForm] = Form.useForm();

    const [nocItem, dispatchNocItem] = useContext(NocItemContext);
    const [stationState, stationDispatch] = useContext(StationContext);

    const notificationApi = useNotificationApi();

    const station = stationState.station;
    const { profileSettings } = station;

    const profiles = nocItem.profiles;
    const { Levels } = nocItem.profiles;

    const { Brand, Customer, Dispenser, POS, Payment } = profiles?.data;
    
    const TOTAL_LEVELS = Object?.keys(Levels)?.length || 0;
    const DEFAULT_OPTION = 'None';
    const CLEAR_PROFILE_ID = 1;

    const [customer, setCustomer] = useState([]);
    const [brand, setBrand] = useState([]);
    const [pos, setPOS] = useState([]);
    const [dispenser, setDispenser] = useState([]);
    const [payment, setPayment] = useState([]);

    const [profileSelection, setProfileSelection] = useState(defaultProfileSettingsObj)
    const [editFormUpdated, setEditFormUpdated] = useState(false);
    const [displayClearWarning, setDisplayClearWarning] = useState(false);
    
    const isProfileCleared = profileSelection?.Customer === CLEAR_PROFILE_ID;

    useEffect(() => {
        if(displayEditProfileForm){
            form.resetFields();
            setDisplayClearWarning(false);
            setEditFormUpdated(false);
            setProfileSelection({
                Customer: profileSettings?.customerID, 
                Brand: profileSettings?.brandID, 
                POS: profileSettings?.posID, 
                Dispenser: profileSettings?.dispenserID,
                Payment: profileSettings?.paymentID, 
            })
        }
    }, [displayEditProfileForm, form, profileSettings])

    useEffect(() => {
        const renderDefaultOptionLast = (arr) => {
            // Sort in a-z order and display default option as last in dropdown
            let defaultOption = arr?.sort((a,b) => a.name.localeCompare(b.name)).find((option) => option.name === DEFAULT_OPTION && !option.parent_id);
            if(defaultOption){
                arr.push(arr.splice(arr.indexOf(defaultOption), 1)[0]);
            }
            return arr;
        }

        const renderLevelOptions = (levelOrder) => {

            if(levelOrder > 0 && levelOrder <= TOTAL_LEVELS){
                if(levelOrder === 1){ 
                    return renderDefaultOptionLast(profiles.data?.Customer)
                } else {
                    let childLevel = Levels[levelOrder]
                    let parentLevel = Levels[levelOrder-1];

                    if(profileSelection?.[parentLevel]){
                        return renderDefaultOptionLast(profiles.data?.[childLevel]?.filter((level) => ((profileSelection?.[parentLevel] === level.parent_id) || (level.name === DEFAULT_OPTION && !level.parent_id))));
                    }

                    return [];
                }
            }
            return [];
        }

        // Levels: 1. Customer, 2. Brand, 3. POS 4. Dispenser 5. Payment
        setCustomer(renderLevelOptions(1));
        setBrand(renderLevelOptions(2));
        setPOS(renderLevelOptions(3))
        setDispenser(renderLevelOptions(4))
        setPayment(renderLevelOptions(5))
        
    }, [form, profileSelection, profiles.data, Levels, TOTAL_LEVELS])


    const handleSaveStationProfiles = (values) => {

        const { Customer, Brand, POS, Dispenser, Payment } = values;

        const checkProfileId = (id) => {
            // if id is from a default option return null
            return profiles?.None?.includes(id) ? null : id;
        }

        const data = {
            ownerID: station.id,
            customerID: checkProfileId(Customer),
            brandID: checkProfileId(Brand),
            posID: checkProfileId(POS),
            dispenserID: checkProfileId(Dispenser),
            paymentID: checkProfileId(Payment),
        }

        saveProfileSettings(data).then((response) => {

            if(response){
                stationDispatch({
                    type: "UPDATE_STATION_INFORMATION", 
                    payload: {profileSettings: data}
                })

                notificationApi.success({
                    message: 'Success!',
                    description: 'Profile settings have been updated.'
                })
            }
            else {
                notificationApi.error({
                    message: 'Error',
                    description: "There was an error updating the station profiles. Please try again."
                })
            }
        })
        setDisplayEditProfileForm(false);
    }

    const handleClearStationProfiles = () => {

        // Clearing profiles assigns the CLEAR PROFILE ID to all properties
        const clearProfileSettings = {
            ownerID: station.id,
            customerID: CLEAR_PROFILE_ID, 
            brandID: CLEAR_PROFILE_ID, 
            posID: CLEAR_PROFILE_ID, 
            dispenserID: CLEAR_PROFILE_ID, 
            paymentID: CLEAR_PROFILE_ID, 
        }

        saveProfileSettings(clearProfileSettings).then(response => {
            if (response){
                stationDispatch({
                    type: 'UPDATE_STATION_INFORMATION', 
                    payload: {profileSettings: clearProfileSettings}
                })

                notificationApi.success({
                    message: 'Success!', 
                    description: 'Profile settings have been updated.'
                })
            }
        })
    }

    const renderProfileSettings = (level, id) => {
        if(id){
            let profile =  level.find((item) => item.id === id)
            return id === CLEAR_PROFILE_ID ? DEFAULT_OPTION : profile?.name
        } 
        return DEFAULT_OPTION;  
    }

    const handleCancelEditProfilesForm = () => {
        form.resetFields();
        setDisplayEditProfileForm(false);
        setDisplayClearWarning(false);
    }

    const renderLevelOptions = (level) => {

        const levelOrder = Object.keys(Levels).find(key => Levels[key] ===  Object.keys(level)[0])

        for(const key in Levels){
            if(key > levelOrder){
                form.setFieldsValue({
                    [Levels[key]]: null
                })
                setProfileSelection({...profileSelection, [Levels[key]]: null})
            }
        }

        setEditFormUpdated(true);
    }

    const formItemConfig = {
        rules: [
            {
                required: true,
                message: '*Required'
            }
        ], 
    }

    const selectConfig = {
        placeholder: 'Please select', 
        allowClear: true, 
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            
    }
    
    const alertMessage = (
        <>
            <Alert 
                type={displayClearWarning ? 'error' : 'info'}
                style={{textAlign: 'center'}}
                description={
                    <>
                        <code><strong>Devices Affected: {dataSource?.length}  </strong></code>
                        { dataSource?.length > 0 && <Divider style={{margin: '8px auto', minWidth: 0, width: '50%'}}/> }
                        { dataSource?.map((device) => <code key={device.id}>{device.deviceType} #{device.serialNum}<br/></code>) }
                    
                        { displayClearWarning && 
                            <>
                                <br/>
                                <br/><code><strong>Profile Selection:</strong></code>
                                <Divider style={{margin: '8px auto', minWidth: 0, width: '50%'}}/>
                                <code>Customer &#8594; None</code><br/>
                                <code>Brand &#8594; None</code><br/>
                                <code>POS &#8594; None</code><br/>
                                <code>Dispenser &#8594; None</code><br/>
                                <code>Payment &#8594; None</code><br/>
                            </>
                        }
                    </>
                }
            />
        </>
    )

    const clearAllProfileFields = () => {

        // Display only
        form.setFieldsValue({
            Customer: DEFAULT_OPTION,
            Brand: DEFAULT_OPTION, 
            POS: DEFAULT_OPTION, 
            Dispenser: DEFAULT_OPTION,
            Payment: DEFAULT_OPTION, 
        });

        setDisplayClearWarning(true);
    }


    const renderCustomerOptions = () => {
        return customer?.filter(profile => profile.id !== CLEAR_PROFILE_ID).map((profile) => ({
            label: profile.name,
            value: profile.id
        }))
    }

    const cancelWarningMode = () => {
        setDisplayClearWarning(false);
        form.resetFields();
        setEditFormUpdated(false);
    }

    const renderProfileFields = (
        <>
            <Form.Item label='Customer:' name='Customer' {...formItemConfig}>
                <Select
                    options={renderCustomerOptions()}
                    onChange={(value) => setProfileSelection({...defaultProfileSettingsObj, Customer: value})}
                    {...selectConfig}
                />
            </Form.Item>
            <Form.Item label='Brand:' name='Brand' {...formItemConfig}>
                <Select
                    disabled={!profileSelection?.Customer || isProfileCleared}
                    options={brand?.map((profile) => ({ 
                            label: profile.name,
                            value: profile.id
                        }))
                    }
                    onChange={(value) => setProfileSelection({...profileSelection, Brand: value, POS: null, Dispenser: null, Payment: null})}
                    {...selectConfig}
                />
            </Form.Item>
            <Form.Item label='POS:' name='POS' {...formItemConfig}>
                <Select
                    disabled={!profileSelection?.Brand || isProfileCleared}
                    options={pos?.map((profile) => ({ 
                            label: profile.name,
                            value: profile.id
                        }))
                    }
                    onChange={(value) => setProfileSelection({...profileSelection, POS: value, Dispenser: null, Payment: null})}
                    {...selectConfig}
                />
            </Form.Item>
            <Form.Item label='Dispenser:' name='Dispenser' {...formItemConfig}>
                <Select
                    disabled={!profileSelection?.POS || isProfileCleared }
                    options={dispenser?.map((profile) => ({ 
                            label: profile.name,
                            value: profile.id
                        }))
                    }
                    onChange={(value) => setProfileSelection({...profileSelection, Dispenser: value, Payment: null})}
                    {...selectConfig}
                />
            </Form.Item>
            <Form.Item label='Payment:' name='Payment' {...formItemConfig}>
                <Select
                    disabled={!profileSelection?.Dispenser || isProfileCleared}
                    options={payment?.map((profile) => ({ 
                            label: profile.name,
                            value: profile.id
                        }))
                    }
                    onChange={(value) => setProfileSelection({...profileSelection, Payment: value})}
                    {...selectConfig}
                />
            </Form.Item>
        </>
    )

    return (
        <>
            { !displayFields && 
                <Card {...props} style={{...props.style, padding: 6 }} >   
                    <Descriptions 
                        layout='horizontal' 
                        size='small' 
                        colon={false}
                        column={1}
                        styles={{ 
                            label: labelStyle 
                        }} 
                        >
                        <Item label='Customer'>{renderProfileSettings(Customer, profileSettings?.customerID)}</Item>
                        <Item label='Brand'>{renderProfileSettings(Brand, profileSettings?.brandID)}</Item>
                        <Item label='POS'>{renderProfileSettings(POS, profileSettings?.posID)}</Item>
                        <Item label='Dispenser'>{renderProfileSettings(Dispenser, profileSettings?.dispenserID)}</Item>
                        <Item label='Payment'>{renderProfileSettings(Payment, profileSettings?.paymentID)}</Item>
                    </Descriptions>
                </Card>
            }

            { displayEditProfileForm &&
                <Modal
                    actionGroup={false}
                    icon={displayClearWarning && <WarningFilled/>}
                    color={ displayClearWarning ? 'var(--error-color)' : 'var(--warning-color)' }
                    justifyTitle='middle'
                    title={`${displayClearWarning ? 'Clear' : 'Edit'} Station Profile`}
                    visible={displayEditProfileForm}
                    setVisible={setDisplayEditProfileForm}
                    width={500}
                    >
                    <>
                        <Form.Item>
                            {alertMessage}
                        </Form.Item>

                        {!displayClearWarning &&
                            <Form 
                                form={form}
                                autoComplete='off'
                                layout='vertical'
                                disabled={displayClearWarning}
                                id={`station-${station.id}-profile-selection-form`}
                                onFinish={(values) => handleSaveStationProfiles(values)}
                                onValuesChange={(changedValues, _) => renderLevelOptions(changedValues)}
                                initialValues={{
                                        Customer: profileSettings?.customerID === CLEAR_PROFILE_ID ? null : profileSettings?.customerID, 
                                        Brand: profileSettings?.brandID === CLEAR_PROFILE_ID ? null : profileSettings?.brandID, 
                                        POS: profileSettings?.posID === CLEAR_PROFILE_ID ? null : profileSettings?.posID, 
                                        Dispenser: profileSettings?.dispenserID === CLEAR_PROFILE_ID ? null : profileSettings?.dispenserID,
                                        Payment: profileSettings?.paymentID === CLEAR_PROFILE_ID ? null : profileSettings?.paymentID, 
                                    }}
                                >
                                    <Form.Item>
                                        <Alert 
                                            description={"Updating the current profile settings will affect any existing SNS-BASE and CS-BASE devices. Are you sure you want to proceed?"}
                                            showIcon={true}
                                            type='warning'
                                            />
                                    </Form.Item>

                                    { renderProfileFields }

                                    { !displayClearWarning &&
                                        <>
                                            { (editFormUpdated && profileSelection?.Payment ) && 
                                                <>
                                                    <Paragraph>Please type <strong>"SAVE"</strong> to confirm: </Paragraph>
                                        
                                                    <Form.Item 
                                                        name='update'
                                                        rules={[
                                                            {
                                                                required: true,
                                                                validateTrigger: 'onSubmit',
                                                                validator: (_, value) => {
                                                                        if(value === 'SAVE'){
                                                                            return Promise.resolve();
                                                                        }
                                                                        return Promise.reject(new Error('Please enter "SAVE" if you want to proceed.'));
                                                                    }
                                                                }
                                                            ]}
                                                        >
                                                        <Input onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                                                    </Form.Item>
                                                </>
                                            }

                                            <Row justify='space-between' align='middle'>
                                                <Button danger onClick={() => clearAllProfileFields()}>Clear Profiles</Button>
                                                <Space>
                                                    <Button onClick={() => handleCancelEditProfilesForm()}>Cancel</Button>
                                                    <Button type='primary' icon={<SaveOutlined/>} htmlType='submit'>Save</Button>
                                                </Space>
                                            </Row>
                                        </>
                                    }
                            </Form>
                        }

                        { displayClearWarning &&
                            <Form
                                form={clearProfilesForm}
                                name={`station-${station.id}-clear-profiles-form`}
                                autoComplete='off'
                                layout='vertical'
                                preserve={false}
                                onFinish={() => {handleClearStationProfiles()}}
                                >

                                <Paragraph style={{textAlign: "center"}}>All profiles will be cleared and any SNS-BASE and CS-BASE devices will be reset to the default configuration. <strong>Are you sure you want to proceed?</strong></Paragraph>
                                <Paragraph style={{textAlign: 'center'}}>Please type <strong>"CLEAR"</strong> to confirm: </Paragraph>
                                
                                <Form.Item 
                                    name='delete'
                                    rules={[
                                        {
                                            required: true,
                                            validateTrigger: 'onSubmit',
                                            validator: (_, value) => {
                                                    if(value === 'CLEAR'){
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Please enter "CLEAR" if you want to proceed.'));
                                                }
                                            }
                                        ]}
                                    >
                                    <Input style={{textAlign: 'center', }} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                                </Form.Item>
                                <Row justify='end'>   
                                    <Form.Item>
                                        <Space>
                                            <Button onClick={() => cancelWarningMode()}>Cancel</Button>
                                            <Button htmlType='submit' type='primary' danger>Yes, clear ALL profiles</Button>
                                        </Space>
                                    </Form.Item>
                                </Row>
                            </Form>
                        }
                    </>
                </Modal>
            }

            { displayFields && 
                renderProfileFields
            }
        </>
    )
}

StationProfile.defaultProps = {
    displayFields: false,
    displayEditProfileForm: false, 
    dataSource: [],
    setDisplayEditProfileForm: undefined,
}
